import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import DevisFormSteps from './DevisFormSteps';
import { sendDevisEmail } from '../../utils/emailjs';
import Toast from './Toast';

interface FormData {
  clientType: string;
  propertyType: string;
  diagnostics: string[];
  name: string;
  email: string;
  phone: string;
  address: string;
  constructionYear: string;
  surface: string;
}

interface DevisFormContentProps {
  formStep: number;
  formData: FormData;
  onStepChange: (step: number) => void;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onDiagnosticToggle: (diagnostic: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const DevisFormContent: React.FC<DevisFormContentProps> = ({
  formStep,
  formData,
  onStepChange,
  onFormChange,
  onDiagnosticToggle,
  onSubmit: parentOnSubmit
}) => {
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [toastType, setToastType] = React.useState<'success' | 'error'>('success');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isStepValid()) return;

    setIsSubmitting(true);
    try {
      await sendDevisEmail(formData);
      setToastMessage('Votre demande a été envoyée avec succès !');
      setToastType('success');
      setShowToast(true);
      parentOnSubmit(e);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du devis:', error);
      setToastMessage('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (formStep) {
      case 1:
        return formData.clientType && formData.diagnostics.length > 0;
      case 2:
        return (
          formData.propertyType && 
          formData.constructionYear && 
          formData.surface
        );
      case 3:
        return formData.name && formData.email && formData.phone;
      default:
        return false;
    }
  };

  return (
    <div className="bg-[#4485b7] rounded-lg p-6 shadow-lg">
      <Toast
        show={showToast}
        message={toastMessage}
        type={toastType}
        onClose={() => setShowToast(false)}
      />

      <div className="flex items-center justify-between mb-8">
        <h3 className="text-white text-2xl font-semibold">Demander un devis</h3>
        <div className="flex items-center space-x-2">
          {[1, 2, 3].map((step) => (
            <motion.div
              key={step}
              initial={{ scale: 0.8 }}
              animate={{
                scale: step === formStep ? 1.2 : 1,
                backgroundColor: step === formStep ? "rgb(255, 255, 255)" : "rgba(255, 255, 255, 0.3)"
              }}
              className="w-2 h-2 rounded-full"
            />
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <AnimatePresence mode="wait">
          <DevisFormSteps
            formStep={formStep}
            formData={formData}
            onFormChange={onFormChange}
            onDiagnosticToggle={onDiagnosticToggle}
          />
        </AnimatePresence>

        <div className="flex justify-between mt-8">
          {formStep > 1 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={() => onStepChange(formStep - 1)}
              className="flex items-center text-white hover:text-white/90 font-medium"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </motion.button>
          )}
          {formStep < 3 ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={() => onStepChange(formStep + 1)}
              disabled={!isStepValid()}
              className={`flex items-center text-white bg-[#3d7aa6] hover:bg-[#376f98] px-6 py-3 rounded-lg ml-auto font-medium ${
                !isStepValid() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Suivant
              <ArrowRight className="h-5 w-5 ml-2" />
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={!isStepValid() || isSubmitting}
              className={`bg-white text-[#4485b7] px-8 py-3 rounded-lg font-medium hover:bg-white/90 ml-auto flex items-center ${
                !isStepValid() || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Envoi en cours...
                </>
              ) : (
                'Envoyer'
              )}
            </motion.button>
          )}
        </div>
      </form>
    </div>
  );
};

export default DevisFormContent;